import { useId, useState } from 'react';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { useAdminMediaLibrary } from '../../media/hooks/use-media.js';

export interface MediaAssetPickerProps {
  value: string;
  onChange: (mediaAssetId: string) => void;
  label?: string;
  /**
   * `image` (default) filters server-side via `useAdminMediaLibrary`'s
   * `kind` param, same as every other picker in the admin app. `video`
   * can't use that same server-side filter -- the admin media list
   * endpoint's `kind` query only accepts `image`/`document` (see
   * `apps/worker/src/modules/catalog/validation/media-schemas.ts`, which
   * this banner-campaign work does not own/touch) even though the
   * `media_assets.kind` column itself does allow `'video'` -- so for
   * `video` this fetches unfiltered and narrows to `asset.kind === 'video'`
   * client-side instead.
   */
  mediaKind?: 'image' | 'video';
}

/**
 * Searchable picker over the existing media library (search box + a
 * scrollable thumbnail grid, selecting sets `mediaAssetId`) built on the
 * same `useAdminMediaLibrary` hook the product-media tab already uses --
 * no new backend endpoint, no dedicated banner-media route. Falls back
 * gracefully to "no results" copy rather than ever blocking banner
 * creation on this picker; the raw UUID stays visible/editable below it
 * so a known asset id can always be pasted in directly.
 */
export function MediaAssetPicker({ value, onChange, label = 'Media asset', mediaKind = 'image' }: MediaAssetPickerProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data, isLoading } = useAdminMediaLibrary({
    search: debouncedSearch || undefined,
    kind: mediaKind === 'image' ? 'image' : undefined,
    limit: mediaKind === 'video' ? 60 : 24,
  });
  const assets = (data ?? []).filter((asset) => (mediaKind === 'video' ? asset.kind === 'video' : true));
  const inputId = useId();

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      <input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search media library…"
        className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
      />
      <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-100 p-2">
        {isLoading && <p className="p-2 text-sm text-gray-400">Loading…</p>}
        {!isLoading && assets.length === 0 && <p className="p-2 text-sm text-gray-400">No media found.</p>}
        {!isLoading && assets.length > 0 && (
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {assets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => onChange(asset.id)}
                aria-pressed={value === asset.id}
                title={asset.id}
                className={`focus-visible:ring-brand aspect-square overflow-hidden rounded-lg border-2 focus-visible:outline-none focus-visible:ring-2 ${
                  value === asset.id ? 'border-gray-900' : 'border-transparent hover:border-gray-200'
                }`}
              >
                {asset.url && asset.kind === 'video' ? (
                  <video src={asset.url} muted className="h-full w-full object-cover" />
                ) : asset.url ? (
                  <img src={asset.url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gray-100 text-[10px] text-gray-400">
                    No preview
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      <div>
        <label htmlFor={inputId} className="mb-1 block text-xs font-medium text-gray-600">
          {label} ID
        </label>
        <input
          id={inputId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 font-mono text-xs focus-visible:outline-none focus-visible:ring-2"
        />
      </div>
    </div>
  );
}
